import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { BlockUI, NgBlockUI } from "ng-block-ui";
import { MessageService } from "primeng/api";
import {
  catchError,
  EMPTY,
  expand,
  filter,
  interval,
  last,
  map,
  mergeMap,
  of,
  retry,
  switchMap,
  take,
  takeWhile,
  timer
} from "rxjs";
import { RestService } from "../../services/rest.service";
import { ToasterService } from "../../services/toaster-service";
import { MessageEnum } from "../../utils/message.enum";
import { TestPrioritizationActions } from "../actions/test-prioritization.action";

@Injectable()
export class TestPrioritizationEffects {
  @BlockUI() blockUI!: NgBlockUI;

  constructor(private actions$: Actions, private restService: RestService, private readonly toaster: ToasterService) {}
  private messageService = inject(MessageService);

  loadTestCases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TestPrioritizationActions.loadTestCases),
      mergeMap(({ mappingId }) =>
        this.restService
          .executeServiceCall("TestCase to Source Code Mappings", mappingId)
          .pipe(
            switchMap((response: any) => {
              if (response?.status === 201) {
                const asyncToken = response?.asyncToken;
                console.log("Code Source Code Mapping ID:", asyncToken);
                // return interval(40000).pipe(
                //   switchMap(() =>
                //     this.restService
                //       .getTestCasesWithId(mappingId, asyncToken)
                //       .pipe(
                //         retry({
                //           count: 1,
                //           delay: (error, retryCount) => {
                //             console.log(`Retry attempt ${retryCount} for getTestCasesWithId`);
                //             return timer(2000);
                //           }
                //         }),
                //         map((statusResponse: any) => {
                //           if (statusResponse?.asyncReqStatus === "Success") {
                //             return TestPrioritizationActions.loadTestCasesSuccess(
                //               {
                //                 testCasesToSourceMapping: statusResponse?.data,
                //               }
                //             );
                //           }
                //           if (statusResponse?.asyncReqStatus === "Failed") {
                //             this.toaster.pop({ type: 'error', text: MessageEnum.SOURCE_CODE_MAPPING_FAILURE });
                //             return TestPrioritizationActions.loadTestCasesFailure(
                //               {
                //                 error: "Source Code Mapping Failed",
                //               }
                //             );
                //           }
                //           return {
                //             type: "[TestPrioritization] Polling In Progress",
                //           };
                //         })
                //       )
                //   ),
                //   takeWhile(
                //     (result) =>
                //       result.type ===
                //       "[TestPrioritization] Polling In Progress",
                //     true
                //   ),
                //   last()
                // );
              
               return this.restService.getTestCasesWithId(mappingId, asyncToken).pipe(
                  expand((response: any) => {
                    const status = response?.data?.status?.toLowerCase();

                    if (status === 'processing') {
                      return timer(40000).pipe(
                        switchMap(() =>
                          this.restService.getTestCasesWithId(mappingId, asyncToken)
                        )
                      );
                    }
                    return EMPTY;
                  }),
                  map((response: any) => {
                    const status = response?.data?.status?.toLowerCase();

                    if (status === 'processing') {
                      return {
                        type: '[TestPrioritization] Polling In Progress',
                      };
                    }

                    if (status === 'completed') {
                      return TestPrioritizationActions.loadTestCasesSuccess({
                        testCasesToSourceMapping: response?.data?.data,
                      });
                    }

                    this.toaster.pop({
                      type: 'error',
                      text: MessageEnum.SOURCE_CODE_MAPPING_FAILURE,
                    });

                    return TestPrioritizationActions.loadTestCasesFailure({
                      error: 'Source Code Mapping Failed',
                    });
                  }),
                  takeWhile(
                    (action) =>
                      action.type === '[TestPrioritization] Polling In Progress',
                    true
                  ),

                  catchError((error) => {
                    this.toaster.pop({
                      type: 'error',
                      text: MessageEnum.SOURCE_CODE_MAPPING_FAILURE,
                    });

                    return of(
                      TestPrioritizationActions.loadTestCasesFailure({
                        error: error?.message || 'Source Code Mapping Failed',
                      })
                    );
                  })
                );
              } else {
                throw new Error("Source Code Mapping Failed");
              }
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.SOURCE_CODE_MAPPING_FAILURE });
              return of(
                TestPrioritizationActions.loadTestCasesFailure({
                  error: error.message,
                })
              );
            })
          )
      )
    )
  );

  createMapping$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TestPrioritizationActions.createMapping),
      switchMap(({ payload }) =>
        this.restService.createMapping(payload).pipe(
          retry({
            count: 1,
            delay: (error, retryCount) => {
              console.log(`Retry attempt ${retryCount} for createMapping`);
              return timer(2000);
            }
          }),
          map((response) => {
            this.blockUI.stop();
            return TestPrioritizationActions.createMappingSuccess({
              response: response?.data,
            });
          }),
          catchError((error: HttpErrorResponse) => {
            this.blockUI.stop();
            this.toaster.pop({ type: 'error', text: MessageEnum.SOURCE_CODE_MAPPING_FAILURE });
            return of(
              TestPrioritizationActions.createMappingFailure({
                error: "Failed to create mapping!",
              })
            );
          })
        )
      )
    )
  );

  updateMapping$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TestPrioritizationActions.updateMapping),
      switchMap(({ payload }) =>
        this.restService.updateMapping(payload).pipe(
          map((response) => {
            this.blockUI.stop();
            this.toaster.pop({ type: 'success', text: MessageEnum.MAPPING_UPDATE_SUCCESS });
            return TestPrioritizationActions.createMappingSuccess({
              response: response?.data,
            });
          }),
          catchError((error) => {
            this.blockUI.stop();
            this.toaster.pop({ type: 'error', text: MessageEnum.MAPPING_UPDATE_FAILURE });
            return of(
              TestPrioritizationActions.createMappingFailure({
                error: "Failed to update mapping!",
              })
            );
          })
        )
      )
    )
  );

  loadRecommendations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TestPrioritizationActions.loadRecommendations),
      mergeMap(({ mappingId }) =>
        this.restService
          .executeServiceCall("Get Recommendations", mappingId)
          .pipe(
            switchMap((response: any) => {
              if (response?.status === 201) {
                const asyncToken = response?.asyncToken;
                console.log("Recommendation ID:", asyncToken);

                return interval(40000).pipe(
                  switchMap(() =>
                    this.restService.handleAsyncRequest(asyncToken).pipe(
                      switchMap((statusResponse: any) => {
                        if (statusResponse?.asyncReqStatus === "Success") {
                          return this.restService
                            .getRecomendationDetails(mappingId)
                            .pipe(
                              map((detailsResponse: any) => {
                                return TestPrioritizationActions.loadRecommendationsSuccess(
                                  {
                                    recommandations: detailsResponse,
                                  }
                                );
                              }),
                              catchError((error) => {
                                this.toaster.pop({ type: 'error', text: error.message || MessageEnum.RECOMMENDATION_FETCHING_FAILURE });
                                return of(
                                  TestPrioritizationActions.loadRecommendationsFailure(
                                    {
                                      error:
                                        error.message ||
                                        "Failed to fetch recommendation details.",
                                    }
                                  )
                                );
                              })
                            );
                        }
                        if (statusResponse?.asyncReqStatus === "Failed") {
                          this.toaster.pop({ type: 'error', text: MessageEnum.RECOMMENDATION_GENERATION_FAILURE });
                          return of(
                            TestPrioritizationActions.loadRecommendationsFailure(
                              {
                                error: "Recommendation generation failed.",
                              }
                            )
                          );
                        }
                        return of({
                          type: "[TestPrioritization] Polling In Progress",
                        });
                      })
                    )
                  ),
                  takeWhile(
                    (result) =>
                      result.type !==
                      TestPrioritizationActions.loadRecommendationsSuccess.type,
                    true
                  ),
                  last()
                );
              } else {
                return of(
                  TestPrioritizationActions.loadRecommendationsFailure({
                    error: "Initial request failed.",
                  })
                );
              }
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: error.message || MessageEnum.RECOMMENDATION_FETCHING_FAILURE });
              return of(
                TestPrioritizationActions.loadRecommendationsFailure({
                  error: error.message || "Something went wrong!",
                })
              );
            })
          )
      )
    )
  );
}
